
    import { defineComponent, PropType, computed, reactive } from 'vue';

    import {
        TreeList,
        filterBy,
        orderBy,
        TreeListColumnProps,
        mapTree,
        extendDataItem,
        TreeListExpandChangeEvent,
        TreeListDataStateChangeEvent
    } from '@progress/kendo-vue-treelist';
    import { Checkbox } from '@progress/kendo-vue-inputs'
    import { Label } from '@progress/kendo-vue-labels'

    import { DbConstants } from '@/const';
    import '@/types/ArrayExtensions'
    import { FilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
    import { buildUnitLocalization, buildClassificationLocalization, buildMaterialLocalization, buildBomItemLocalization } from '@/util'
    import { BomHeaderToTreeNodeStructureTransformer } from '@/lib/bom'
    import { IBomHeaderDTO , IBomTreeNode, BomTreeNodeType } from '@/types/bom'

    interface IEquipmentTreeListData {
        filterEnabled: boolean,
        subItemsField: string,
        expandField: string,
        expanded: number[],
        filter: FilterDescriptor[],
        sort: SortDescriptor[]
    }

    interface IListItem {
        id: number,
        nodeType?: BomTreeNodeType
        description: string,
        status?: string,

        materialNo?: number,
        isSparePart?: boolean,
        price?: number,
        priceQtyAndUnit?: string | null,

        sparePartCategory: string | null,
        sparePartPackage: string | null,

        hasProductionBom?: boolean,
        hasSeveralAlternativeBoms?: boolean,
        hasMaintenanceBom?: boolean,
        hasBoughtWithBom?: boolean,
        hasReplacement?: boolean,

        isBulk?: boolean,
        isFixedQty?: boolean,

        qty?: number,
        unit?: string,

        warning?: string,

        childItems?: IListItem[]
    }

    export default defineComponent({
        name: 'EquipmentTreeList',

        components: {
            'k-label': Label,
            Checkbox,
            TreeList
        },
        /********************************************************************************************************************
         PUBLIC PROPERTIES
         ********************************************************************************************************************/
        props: {
            bomData: { type: Object as PropType<IBomHeaderDTO>, required: false }
        },

        setup(props) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const currencyFormatter = new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'EUR' })
            const numberFormatter = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 3 })

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const data = reactive({
                filterEnabled: false,
                subItemsField: 'childItems',
                expandField: 'expanded',
                expanded: [],
                filter: [],
                sort: []
            //    [{
            //        field: 'description',
            //        dir: 'asc'
            //    }]
            } as IEquipmentTreeListData);
  
            const { materialTextFor, localize } = buildMaterialLocalization();
            const { localizeBomItemText } = buildBomItemLocalization()
            const { localizeUnit3 } = buildUnitLocalization();
            const { localizePropValueHierarchy } = buildClassificationLocalization();
                
            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const materialNo = computed( () =>                 
                props.bomData?.material.materialNo                 
            )

            const materialText = computed( () => materialTextFor(props.bomData?.material))

            const materialDescription = computed( () => 
                materialNo.value + ' ' + materialText.value                
            )

            const listData = computed( () =>                 
                props.bomData == null
                    ? []
                    : transformStart(props.bomData)
            )

            const processedData = computed( () => {
                const filteredData = filterBy(listData.value, data.filter, data.subItemsField);
                const sortedData = orderBy(filteredData, data.sort, data.subItemsField);
                return addExpandField(sortedData);
            })

            const columnDef = computed(() => [
                {
                    field: 'materialNo',
                    title: localize('MaterialNo'),
                    expandable: true,
                    className: 'materialCol',
                    locked: true
                },
                {
                    field: 'description',
                    title: localize('Description'),
                },
                {
                    field: 'qty',
                    title: localize('Qty'),
                    filter: "numeric",
                    cell: "renderQty",
                    width: 175
                },
                {
                    field: 'id',
                    title: ' ',
                    cell: 'renderIcons',
                    sortable: false,
                    filterable: false
                },
                {
                    field: 'status',
                    title: localize('Status'),
                    width: 175
                },
                {
                    field: 'sparePartCategory',
                    title: localize('SparePartType'),
                    width: 175
                },
                {
                    field: 'sparePartPackage',
                    title: localize('SparePartPackage'),
                    width: 175
                },
                {
                    field: 'price',
                    title: localize('Price'),
                    cell: "renderPrice",
                    width: 100,
                    filterable: false
                },
                {
                    field: 'priceQtyAndUnit',
                    title: localize('PriceQty'),
                    cell: "renderRightJustified",
                    width: 75,
                    filterable: false
                },
                {
                    field: 'warning',
                    title: localize('Warning'),
                    className: 'redText',
                    width: 200
                },
                {
                    field: 'isBulk',
                    title: localize('IsBulk'),
                    filter: 'boolean',
                    width: 100
                },
                {
                    field: 'isSparePart',
                    title: localize('SparePart'),
                    filter: 'boolean',
                    width: 100
                },
            ] as TreeListColumnProps[])

            const iconPathProduction = computed(() => getIconPath('production'))
            const iconPathAlternative = computed(() => getIconPath('alternatives'))
            const iconPathBoughtTogether = computed(() => getIconPath('recommended-items'))
            const iconPathMaintenance = computed(() => getIconPath('maintenance'))
            const iconPathReplaced = computed(() => getIconPath('replaced'))
            const iconPathWarning = computed(() => require('@/img/icon-warning.png'))

            const computedData = { materialNo, materialText, materialDescription, listData, processedData, columnDef,
                iconPathProduction, iconPathMaintenance, iconPathBoughtTogether, iconPathAlternative, iconPathReplaced, iconPathWarning
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function transformStart(data: IBomHeaderDTO): IListItem[] {
                // Explode BOMs
                const bomTransformer = new BomHeaderToTreeNodeStructureTransformer(
                    item => materialTextFor(item.material),
                    localize,
                    localizeBomItemText
                );
                const nodes: IBomTreeNode[] = bomTransformer.transform(data);

                // Extract Equipment maintenance items
                if (nodes.length === 1 && nodes[0].childNodes[0]?.nodeType == BomTreeNodeType.MaintenanceNode) {
                    const equipmentMaintenanceNodes = nodes[0].childNodes[0].childNodes;
                    return transformToListItems(equipmentMaintenanceNodes);
                }

                return [];
            }

            function transformToListItems(nodes: IBomTreeNode[]): IListItem[] {{
                return nodes.map(node => transformNode(node));
            }}

            function transformNode(node: IBomTreeNode): IListItem {
                const material = node.bomItem?.material;
                const childItems = transformToListItems(node.childNodes);

                // Description
                let description = '';

                if (node.description) {
                    description = node.description;
                }
                else if (material) {
                    description = materialTextFor(material)
                }

                // Status text
                let statusText: string = '';
                
                if (node.nodeType == BomTreeNodeType.ReplacementNode) {
                    statusText = `Erstatning for ${node.parentBomItem?.material.materialNo}`
                }
                else if (node.nodeType == BomTreeNodeType.Material && material) {
                    statusText = getPropValueText(DbConstants.sparePartPropertyNames.sparePartStatus, material.sparePartStatus);
                }

                // Warning text
                let warningText = '';                               
                if (node.bomItem && !node.bomItem.isBulk) {
                    warningText = getWarningText(node.bomItem.qty, node.bomItem.actualQty)
                }

                return {
                    id: node.nodeId,
                    nodeType: node.nodeType,
                    materialNo: material?.materialNo,
                    description,                    
                    status: statusText,
                    qty: node.qty,
                    unit: node.unit,                    
                    isBulk: node.bomItem?.isBulk,
                    isFixedQty: node.bomItem?.isFixedQty,
                    isSparePart: material?.isSparePart,
                    sparePartCategory: getPropValueText(DbConstants.sparePartPropertyNames.sparePartType, material?.sparePartType),
                    sparePartPackage: getPropValueText(DbConstants.sparePartPropertyNames.sparePartPackage, material?.sparePartPackage),
                    
                    price: material?.price,

                    priceQtyAndUnit: material?.priceQty
                        ? `${material.priceQty} ${localizeUnit3(material.priceQtyUnit)}`
                        : null,

                    warning: warningText,

                    hasProductionBom: node.hasProductionBom,
                    hasSeveralAlternativeBoms: node.hasSeveralAlternativeBoms,
                    hasBoughtWithBom: node.hasBoughtWithBom,
                    hasMaintenanceBom: node.hasMaintenanceBom,
                    hasReplacement: node.hasReplacement,

                    childItems: childItems
                } as IListItem;
            }

            function getWarningText(qty: number, actualQty?: number) {
                if (actualQty == null ||qty === actualQty) return '';

                if (qty == 0 && actualQty > 0) {
                    return `Ikke planlagt vareafgang (Planlagt=0, Faktisk=${actualQty})`;
                }

                if (qty > 0 && actualQty == 0) {
                    return `Vareafgang mangler (Planlagt=${qty}, Faktisk=0)`;
                }

                return `Vareafgang afviger (Planlagt=${qty}, Faktisk=${actualQty})`;            
            }

            function createAppState(dataState: { sort?: SortDescriptor[], filter?: FilterDescriptor[] }) {
                data.sort = dataState.sort ?? [];
                data.filter = dataState.filter ?? [];
            }

            function addExpandField(dataTree: IListItem[]): any[] {
                const expandedState = data.expanded;
                return mapTree(dataTree,
                    data.subItemsField,
                    item => extendDataItem(item, data.subItemsField, {
                        [data.expandField]: expandedState.includes(item.id)
                    })
                )
            }

            function getIconPath(iconName: string): string {
                return require(`@/img/icon-${iconName}.svg`);
            }

            function getPropValueText(propertyName: string, propertyValue?: string | null): string {
                if (!propertyValue) return '';

                const propertyNames = localizePropValueHierarchy(propertyName, propertyValue);
                const result = propertyNames.join('/');

                //console.log(`${propertyName}, ${propertyValue}, ${result}`);
                return result;
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/           
            function formatPrice(price: number | null): string {
                return price
                    ? currencyFormatter.format(price)
                    : '';
            }

            function formatQty(dataItem: IListItem): string {
                return dataItem?.qty
                    ? `${numberFormatter.format(dataItem.qty)} ${dataItem?.unit ? localizeUnit3(dataItem.unit) : ''}`
                    : '';
            }

            function onExpandChange(e: TreeListExpandChangeEvent) {
                data.expanded = e.value
                    ? data.expanded.filter(id => id !== e.dataItem.id)
                    : [...data.expanded, e.dataItem.id];
            }

            function handleDataStateChange(e: TreeListDataStateChangeEvent) {
                createAppState(e.data);
            }            

            /********************************************************************************************************************
             LIFECYCLE HOOKS
             ********************************************************************************************************************/
            //console.log('EquipmentTreeList: Created.');
            //console.log(props.bomData);

            /********************************************************************************************************************
             INTERFACE
             ********************************************************************************************************************/
            return {
                // DATA
                data,
                // COMPUTED
                ...computedData,
                // METHODS AND EVENT HANDLERS
                localize, onExpandChange, handleDataStateChange, formatPrice, formatQty
            }
        }
    })

